// SPDX-License-Identifier: AGPL-3.0-or-later

import type {GuildBan} from '@app/features/guild/commands/GuildCommands';
import styles from '@app/features/guild/components/modals/guild_tabs/GuildMemberActionsSheet.module.css';
import {BanDetailsModal} from '@app/features/moderation/components/modals/BanDetailsModal';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import * as TextCopyCommands from '@app/features/ui/commands/TextCopyCommands';
import {MenuBottomSheet, type MenuGroupType} from '@app/features/ui/menu_bottom_sheet/MenuBottomSheet';
import * as AvatarUtils from '@app/features/user/utils/AvatarUtils';
import {useLingui} from '@lingui/react/macro';
import {EyeIcon, IdentificationCardIcon, ProhibitIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';

interface BannedUserActionsSheetProps {
	isOpen: boolean;
	onClose: () => void;
	ban: GuildBan;
	onRevoke: () => void;
}
export const BannedUserActionsSheet: React.FC<BannedUserActionsSheetProps> = observer(
	({isOpen, onClose, ban, onRevoke}) => {
		const {t, i18n} = useLingui();
		const {user} = ban;
		const userTag = user.tag ?? `${user.username}#${(user.discriminator ?? '').padStart(4, '0')}`;
		const handleViewDetails = () => {
			onClose();
			ModalCommands.push(modal(() => <BanDetailsModal ban={ban} onRevoke={onRevoke} />));
		};
		const handleRevokeBan = () => {
			onClose();
			onRevoke();
		};
		const handleCopyUserId = () => {
			TextCopyCommands.copy(i18n, user.id, true);
			onClose();
		};
		const menuGroups: Array<MenuGroupType> = [
			{
				items: [
					{
						icon: <EyeIcon className={styles.icon} weight="bold" />,
						label: t`View Details`,
						onClick: handleViewDetails,
					},
				],
			},
			{
				items: [
					{
						icon: <IdentificationCardIcon className={styles.icon} weight="bold" />,
						label: t`Copy User ID`,
						onClick: handleCopyUserId,
					},
				],
			},
			{
				items: [
					{
						icon: <ProhibitIcon className={styles.icon} weight="bold" />,
						label: t`Revoke Ban`,
						onClick: handleRevokeBan,
						danger: true,
					},
				],
			},
		];
		const avatarUrl = AvatarUtils.getUserAvatarURL(user, false);
		const headerContent = (
			<div className={styles.header}>
				<img src={avatarUrl} alt="" className={styles.headerAvatarImg} />
				<div className={styles.headerInfo}>
					<span className={styles.headerName}>{user.username}</span>
					<span className={styles.headerTag}>{userTag}</span>
				</div>
			</div>
		);
		return <MenuBottomSheet isOpen={isOpen} onClose={onClose} groups={menuGroups} headerContent={headerContent} />;
	},
);
